import { cardAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, cssVar } from '@chakra-ui/react';
import { Colors, Radii, Shadows, Space, Sizes } from '../../types';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(cardAnatomy.keys);

const $bg = cssVar('card-bg');
const $padding = cssVar('card-padding');
const $paddingY = cssVar('card-padding-y');
const $shadow = cssVar('card-shadow');
const $radius = cssVar('card-radius', 'var(--udl-radii-md)');
const $borderColor = cssVar('card-border-color', 'transparent');
const $border = cssVar('card-border', '0');

const baseStyle = definePartsStyle(props => {
  const { direction } = props;
  return {
    container: {
      background: $bg.reference,
      borderWidth: $border.reference,
      borderColor: $borderColor.reference,
      borderRadius: $radius.reference,
      height: Sizes.FULL,
      '& > img:first-child, & > div:first-child:has(img)': {
        roundedTop: direction === 'column' ? 'inherit' : undefined,
      },
    },
    header: {
      padding: $padding.reference,
      color: Colors.TEXT_EMPHASIS,
      _notFirst: {
        pt: direction === 'row' ? $padding.reference : $paddingY.reference,
      },
      _notLast: {
        pb: direction === 'row' ? $padding.reference : $paddingY.reference,
      },
    },
    body: {
      padding: $padding.reference,
      align: 'baseline',
      color: Colors.TEXT_MUTED,
      maxH: 'full',
      _notFirst: {
        pt: direction === 'row' ? $padding.reference : $paddingY.reference,
      },
      _notLast: {
        pb: direction === 'row' ? $padding.reference : $paddingY.reference,
      },
    },
    footer: {
      padding: $padding.reference,
      color: Colors.TEXT_MUTED,
      justifyContent: 'end',
      gap: Space.XS,
      _notFirst: {
        pt: direction === 'row' ? $padding.reference : $paddingY.reference,
      },
      _notLast: {
        pb: direction === 'row' ? $padding.reference : $paddingY.reference,
      },
    },
  };
});

const variants = {
  elevated: definePartsStyle({
    container: {
      [$bg.variable]: `colors.${Colors.SURFACE_PAGE}`,
      [$borderColor.variable]: `colors.transparent`,
      outline: '1px solid',
      outlineColor: $borderColor.reference,
      boxShadow: $shadow.reference,
      _dark: {
        [$bg.variable]: `colors.${Colors.SURFACE_ELEVATED}`,
        [$borderColor.variable]: `colors.${Colors.BORDER_ELEVATED}`,
      },
    },
  }),
  outline: definePartsStyle({
    container: {
      [$bg.variable]: `colors.${Colors.SURFACE_PAGE}`,
      [$borderColor.variable]: `colors.${Colors.BORDER_DEFAULT}`,
      [$border.variable]: '1px',
      [$shadow.variable]: 'none',
      boxShadow: Shadows.NONE,
    },
  }),
  filled: definePartsStyle({
    container: {
      [$bg.variable]: `colors.${Colors.SURFACE_TERTIARY}`,
      [$borderColor.variable]: `colors.${Colors.TRANSPARENT}`,
      [$shadow.variable]: 'none',
      boxShadow: Shadows.NONE,
      _dark: {
        [$bg.variable]: `colors.${Colors.SURFACE_TERTIARY}`,
      },
    },
  }),
  unstyled: definePartsStyle({
    container: {
      padding: 0,
    },
  }),
};

const sizes = {
  sm: definePartsStyle({
    container: {
      [$radius.variable]: `radii.${Radii.LG}`,
      [$padding.variable]: `space.${Space.SM}`,
      [$paddingY.variable]: `space.${Space['2XS']}`,
      '&': {
        [$shadow.variable]: `shadows.${Shadows.XS}`,
      },
    },
  }),
  md: definePartsStyle({
    container: {
      [$radius.variable]: `radii.${Radii.XL}`,
      [$padding.variable]: `space.${Space.MD}`,
      [$paddingY.variable]: `space.${Space.XS}`,
      '&': {
        [$shadow.variable]: `shadows.${Shadows.SM}`,
      },
    },
  }),
  lg: definePartsStyle({
    container: {
      [$radius.variable]: `radii.${Radii['2XL']}`,
      [$padding.variable]: `space.${Space.LG}`,
      [$paddingY.variable]: `space.${Space.XS}`,
      '&': {
        [$shadow.variable]: `shadows.${Shadows.MD}`,
      },
    },
  }),
};
// define custom variant

// export the component theme
export const Card = defineMultiStyleConfig({
  baseStyle,
  sizes,
  variants,
  defaultProps: {
    // default variant
    variant: 'elevated',
  },
});
